/* Collection bento: filters, sorting and pagination for
   `sections/main-collection-bento.liquid` + `snippets/collection-filters-bento.liquid`.

   The markup already works without this file: the filter form is a plain
   GET form (the sort select joins it through `form="CollFiltersForm-{id}"`)
   and every pill, clear and pagination link is a real URL. This script only
   upgrades that: a change fetches the section through the Section Rendering
   API (`?section_id=`), swaps the parts that depend on the result set and
   records the state with `history.pushState`, so back/forward keep working.

   Load more (the section's default pagination): `[data-coll-more="next"]`
   appends the next page's cards to `[data-coll-grid]` and
   `[data-coll-more="previous"]` puts the earlier page's above them (the
   view stays on the same card), each followed by the fetched page's own
   slot; the grid's `data-first-page` / `data-last-page` say what is on
   screen, and the address follows the last page (`history.replaceState`,
   no new entry). On leaving, the first page and the scroll position are
   kept in sessionStorage for that address; coming back to it without the
   page in the back/forward cache, the earlier pages are fetched again and
   the view is put back where it was. Without the script the buttons are
   plain links to the next and previous page.

   The DOM contract lives in docs/collection-bento.md („Kontrakt DOM”). What gets swapped
   after a fetch: `[data-coll-results]`, `[data-coll-count]`,
   `[data-coll-active]`, `[data-coll-active-count]`, `[data-coll-show-results]`,
   the `href` of every `[data-coll-clear]`, and per `[data-coll-group]` its
   `[data-coll-group-body]` / `[data-coll-group-count]`. The form element
   itself is never replaced (only group bodies inside it), so it can live in
   the drawer while results update.

   Columns switcher: `[data-coll-cols]` buttons (set "phone" or "desk")
   store the choice in localStorage `lt-coll-cols` and set
   `data-cols-phone` / `data-cols-desk` on the root, which the CSS turns
   into the grid's column count (the section's inline script does the same
   before the first paint). `aria-pressed` follows the count actually on
   screen (`--m-coll-cols`), across breakpoints and the filter toggle, and
   the cards' image `sizes` follow the choice, including cards added later.

   View switcher: `[data-coll-view]` buttons ("product" or "lifestyle")
   store the choice in localStorage `lt-coll-view` and set or drop
   `data-view="lifestyle"` on the root, which the CSS turns into the card's
   second image on top (the section's inline script restores it before the
   first paint). `aria-pressed` follows the root.

   One instance per section root, keyed by section id so
   `shopify:section:unload` tears down exactly that one. The root element is
   the idempotency guard: the editor re-renders a section into a fresh root. */
(function () {
  /* Same top-level guard as `product-list-bento.js`: the editor can re-inject
     this script tag, and a second run would double the document listeners. */
  var behaviourRoot = document.documentElement;
  if (behaviourRoot.dataset.mCollBehaviour === 'true') return;
  behaviourRoot.dataset.mCollBehaviour = 'true';

  var STORAGE_KEY = 'lt-coll-filters-hidden';
  var MORE_KEY = 'lt-coll-more';
  var COLS_KEY = 'lt-coll-cols';
  var COLS_ALLOWED = { phone: ['1', '2'], desk: ['3', '4', '6'] };
  var VIEW_KEY = 'lt-coll-view';
  var LOCK_CLASS = 'm-coll-lock';
  var PRICE_DELAY = 500;
  var DESKTOP_QUERY = '(min-width: 1024px)';

  var instances = {};

  function each(list, callback) {
    Array.prototype.forEach.call(list, callback);
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ---- storage ---------------------------------------------------------- */

  /* Storage can throw (private mode, blocked site data), even on access. */
  /* '1' hidden, '0' shown; nothing stored = the section default. Both
     values are written, because the default can be either. Session, not
     local storage: the choice follows the shopper between collections, but
     a new visit starts from the default (owner, 2026-09-24 — a remembered
     "shown" made the card look open by default). */
  function readHidden(fallback) {
    try {
      var stored = window.sessionStorage.getItem(STORAGE_KEY);
      if (stored === '1') return true;
      if (stored === '0') return false;
    } catch (error) {
      /* Fall through to the default. */
    }
    return fallback;
  }

  function writeHidden(hidden) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, hidden ? '1' : '0');
    } catch (error) {
      /* Not remembered - the toggle still works for this page view. */
    }
  }

  /* ---- DOM helpers ------------------------------------------------------ */

  /* Updates `current` in place from `next` (attributes + content) instead of
     swapping the node: `[data-coll-count]` is a live region, and a region
     inserted fresh is not announced. Unchanged markup is left untouched. */
  function syncElement(current, next) {
    var i;
    for (i = current.attributes.length - 1; i >= 0; i--) {
      var name = current.attributes[i].name;
      if (!next.hasAttribute(name)) current.removeAttribute(name);
    }
    for (i = 0; i < next.attributes.length; i++) {
      if (current.getAttribute(next.attributes[i].name) !== next.attributes[i].value) {
        current.setAttribute(next.attributes[i].name, next.attributes[i].value);
      }
    }
    if (current.innerHTML !== next.innerHTML) current.innerHTML = next.innerHTML;
  }

  /* Pairs live and fetched elements by document order. */
  function syncAll(root, nextRoot, selector) {
    var current = root.querySelectorAll(selector);
    var next = nextRoot.querySelectorAll(selector);
    for (var i = 0; i < current.length && i < next.length; i++) syncElement(current[i], next[i]);
  }

  /* Links rendered through `?section_id=` (remove-links, pagination) can
     carry that param back; opened in a new tab they would show the bare
     section, so it is stripped from every swapped-in href. */
  function stripSectionId(scope) {
    if (typeof URL !== 'function') return;
    each(scope.querySelectorAll('a[href*="section_id="]'), function (link) {
      try {
        var url = new URL(link.getAttribute('href'), location.href);
        url.searchParams.delete('section_id');
        link.setAttribute('href', url.origin === location.origin ? url.pathname + url.search + url.hash : url.href);
      } catch (error) {
        /* An unparsable href stays as rendered. */
      }
    });
  }

  /* Clear links outside the containers that are swapped whole. */
  function loneClears(scope) {
    return Array.prototype.filter.call(scope.querySelectorAll('[data-coll-clear]'), function (link) {
      return !link.closest('[data-coll-results], [data-coll-active]');
    });
  }

  /* Drops `section_id` and empty values: an empty price field means "no
     bound", and Shopify's own remove-links carry `filter.v.price.gte=`. */
  function cleanParams(source) {
    var params = new URLSearchParams();
    source.forEach(function (value, key) {
      if (key === 'section_id' || typeof value !== 'string' || value.trim() === '') return;
      params.append(key, value);
    });
    return params;
  }

  /* ---- price range ------------------------------------------------------ */

  function priceParts(wrap) {
    var parts = {
      wrap: wrap,
      rangeMin: wrap.querySelector('[data-coll-price-range="min"]'),
      rangeMax: wrap.querySelector('[data-coll-price-range="max"]'),
      inputMin: wrap.querySelector('[data-coll-price-input="min"]'),
      inputMax: wrap.querySelector('[data-coll-price-input="max"]')
    };
    var max = parseFloat(wrap.getAttribute('data-max'));
    if (!(max > 0) && parts.rangeMax) max = parseFloat(parts.rangeMax.max);
    parts.max = max > 0 ? max : 0;
    return parts;
  }

  function percent(value, max) {
    if (!max) return '0%';
    var ratio = Math.max(0, Math.min(1, value / max));
    return Math.round(ratio * 10000) / 100 + '%';
  }

  function paintPrice(parts) {
    var low = parts.rangeMin ? Number(parts.rangeMin.value) : 0;
    var high = parts.rangeMax ? Number(parts.rangeMax.value) : parts.max;
    parts.wrap.style.setProperty('--m-coll-range-min', percent(low, parts.max));
    parts.wrap.style.setProperty('--m-coll-range-max', parts.max ? percent(high, parts.max) : '100%');
    /* The max input is painted on top, so two thumbs parked at the top end
       would leave the min one unreachable; lift it once it passes halfway. */
    if (parts.rangeMin) parts.rangeMin.style.zIndex = parts.max && low > parts.max / 2 ? '2' : '';
  }

  /* The number fields are the source of truth (they carry the `name`); the
     thumbs follow them. Empty = no bound = thumb at its end. */
  function rangesFromInputs(parts) {
    var low = parseFloat(parts.inputMin ? parts.inputMin.value : '');
    var high = parseFloat(parts.inputMax ? parts.inputMax.value : '');
    if (parts.rangeMin) parts.rangeMin.value = isFinite(low) ? Math.max(0, Math.min(parts.max, low)) : 0;
    if (parts.rangeMax) parts.rangeMax.value = isFinite(high) ? Math.max(0, Math.min(parts.max, high)) : parts.max;
    paintPrice(parts);
  }

  function initPrice(wrap) {
    var parts = priceParts(wrap);
    /* `data-max` is the contract; the thumbs must share it, or a range
       without its own `max` attribute would stop at the default 100. */
    if (parts.max) {
      [parts.rangeMin, parts.rangeMax].forEach(function (range) {
        if (!range) return;
        range.min = 0;
        range.max = parts.max;
      });
    }
    rangesFromInputs(parts);
  }

  /* A thumb moved: keep min <= max, mirror it into its number field. A thumb
     at its end clears the field, so the filter drops that bound entirely. */
  function onRangeInput(range) {
    var parts = priceParts(range.closest('[data-coll-price]'));
    var isMin = range === parts.rangeMin;
    if (isMin && parts.rangeMax && Number(range.value) > Number(parts.rangeMax.value)) range.value = parts.rangeMax.value;
    if (!isMin && parts.rangeMin && Number(range.value) < Number(parts.rangeMin.value)) range.value = parts.rangeMin.value;
    var value = Number(range.value);
    var input = isMin ? parts.inputMin : parts.inputMax;
    if (input) {
      if (isMin) input.value = value <= 0 ? '' : String(value);
      else input.value = value >= parts.max ? '' : String(value);
    }
    paintPrice(parts);
  }

  /* Runs on `change` (blur / Enter), not while typing: rewriting a field
     mid-keystroke would fight the shopper. Clamps to the range and swaps a
     reversed pair. */
  function normalisePrice(wrap) {
    var parts = priceParts(wrap);
    if (!parts.inputMin || !parts.inputMax) return;
    var low = parseFloat(parts.inputMin.value);
    var high = parseFloat(parts.inputMax.value);
    if (isFinite(low) && low < 0) low = 0;
    if (parts.max && isFinite(high) && high > parts.max) high = parts.max;
    if (isFinite(low) && isFinite(high) && low > high) {
      var swap = low;
      low = high;
      high = swap;
    }
    parts.inputMin.value = isFinite(low) ? String(low) : '';
    parts.inputMax.value = isFinite(high) ? String(high) : '';
    rangesFromInputs(parts);
  }

  /* ---- instance --------------------------------------------------------- */

  function init(root) {
    var sectionId = root.getAttribute('data-section-id');
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var options = controller ? { signal: controller.signal } : false;

    var dialog = root.querySelector('dialog[data-coll-drawer]');
    var drawerOpen = false;
    var opener = null;

    var request = null;
    var sequence = 0;
    var priceTimer = null;
    /* The URL whose state is on screen or on its way: repeated changes that
       land on the same URL (a re-typed price, the current page number) do
       not fetch again, and `popstate` ignores hash-only moves. */
    var currentUrl = location.pathname + location.search;
    var basePath = location.pathname;
    var moreBusy = false;

    function getForm() {
      return root.querySelector('[data-coll-form]');
    }

    /* ---- columns switcher ---- */

    function readCols() {
      var chosen = null;
      try {
        chosen = JSON.parse(window.localStorage.getItem(COLS_KEY) || 'null');
      } catch (error) {
        chosen = null;
      }
      var result = {};
      if (chosen && typeof chosen === 'object') {
        Object.keys(COLS_ALLOWED).forEach(function (set) {
          if (COLS_ALLOWED[set].indexOf(chosen[set]) !== -1) result[set] = chosen[set];
        });
      }
      return result;
    }

    /* The chosen widths as `sizes`; the parts not chosen keep the
       section's defaults (snippets/collection-bento-page.liquid). */
    function colsSizes(chosen) {
      var desk = Number(chosen.desk) || 0;
      var phone = Number(chosen.phone) || 2;
      var wide = desk ? Math.round(100 / desk) + 'vw' : '20vw';
      var narrow = desk ? Math.round(100 / (desk === 6 ? 4 : desk)) + 'vw' : '25vw';
      return '(min-width: 1280px) ' + wide + ', (min-width: 1024px) ' + narrow + ', (min-width: 768px) 33vw, ' + Math.round(100 / phone) + 'vw';
    }

    function paintCols() {
      var chosen = readCols();
      var shown = String(parseInt(window.getComputedStyle(root).getPropertyValue('--m-coll-cols'), 10));
      each(root.querySelectorAll('[data-coll-cols]'), function (button) {
        button.setAttribute('aria-pressed', button.value === shown ? 'true' : 'false');
      });
      if (!chosen.phone && !chosen.desk) return;
      var sizes = colsSizes(chosen);
      each(root.querySelectorAll('[data-coll-grid] img[sizes]'), function (image) {
        if (image.getAttribute('sizes') !== sizes) image.setAttribute('sizes', sizes);
      });
    }

    function chooseCols(button) {
      var set = button.getAttribute('data-coll-cols');
      if (!COLS_ALLOWED[set] || COLS_ALLOWED[set].indexOf(button.value) === -1) return;
      var chosen = readCols();
      chosen[set] = button.value;
      try {
        window.localStorage.setItem(COLS_KEY, JSON.stringify(chosen));
      } catch (error) {
        /* Not remembered: this page still switches. */
      }
      root.setAttribute(set === 'phone' ? 'data-cols-phone' : 'data-cols-desk', button.value);
      paintCols();
    }

    /* ---- view switcher ---- */

    function paintView() {
      var view = root.getAttribute('data-view') === 'lifestyle' ? 'lifestyle' : 'product';
      each(root.querySelectorAll('[data-coll-view]'), function (button) {
        button.setAttribute('aria-pressed', button.value === view ? 'true' : 'false');
      });
    }

    function chooseView(button) {
      var view = button.value === 'lifestyle' ? 'lifestyle' : 'product';
      try {
        window.localStorage.setItem(VIEW_KEY, view);
      } catch (error) {
        /* Not remembered: this page still switches. */
      }
      if (view === 'lifestyle') root.setAttribute('data-view', 'lifestyle');
      else root.removeAttribute('data-view');
      paintView();
    }

    var colsFrame = 0;
    function scheduleCols() {
      if (colsFrame) return;
      // A timer, not requestAnimationFrame: a hidden page never runs the
      // frame, and the pending flag would then block every later repaint.
      colsFrame = window.setTimeout(function () {
        colsFrame = 0;
        paintCols();
      }, 0);
    }

    /* Cards come and go (filters, load more): their images get the sizes
       too. The breakpoints and the filter toggle change the count on
       screen: the pressed button follows. */
    var colsObserver = typeof MutationObserver === 'function' ? new MutationObserver(scheduleCols) : null;
    if (colsObserver && root.querySelector('[data-coll-cols]')) {
      colsObserver.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-filters-hidden'] });
    }
    var colsQueries = ['(min-width: 768px)', '(min-width: 1024px)', '(min-width: 1280px)'].map(function (query) {
      var list = window.matchMedia ? window.matchMedia(query) : null;
      if (list) {
        if (typeof list.addEventListener === 'function') list.addEventListener('change', scheduleCols);
        else if (typeof list.addListener === 'function') list.addListener(scheduleCols);
      }
      return list;
    });

    /* ---- hide filters (desktop) ---- */

    function applyHidden(hidden) {
      if (hidden) root.setAttribute('data-filters-hidden', '');
      else root.removeAttribute('data-filters-hidden');
      each(root.querySelectorAll('[data-coll-filters-toggle]'), function (button) {
        button.setAttribute('aria-expanded', hidden ? 'false' : 'true');
        var hideLabel = button.querySelector('[data-coll-toggle-hide]');
        var showLabel = button.querySelector('[data-coll-toggle-show]');
        if (hideLabel) hideLabel.hidden = hidden;
        if (showLabel) showLabel.hidden = !hidden;
      });
    }

    function toggleFilters() {
      var hidden = !root.hasAttribute('data-filters-hidden');
      applyHidden(hidden);
      writeHidden(hidden);
    }

    /* ---- drawer (phones) ---- */

    function moveForm(target) {
      var form = getForm();
      if (form && target && form.parentNode !== target) target.appendChild(form);
    }

    function openDrawer(button) {
      if (!dialog || drawerOpen) return;
      drawerOpen = true;
      opener = button;
      moveForm(dialog.querySelector('[data-coll-drawer-body]'));
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      document.documentElement.classList.add(LOCK_CLASS);
      if (opener) opener.setAttribute('aria-expanded', 'true');
    }

    /* Runs from the explicit close path and from the dialog's `close` event;
       the flag makes the second call a no-op, and the explicit path does not
       depend on `close` ever firing. */
    function afterClose() {
      if (!drawerOpen) return;
      drawerOpen = false;
      moveForm(root.querySelector('[data-coll-filters-slot]'));
      document.documentElement.classList.remove(LOCK_CLASS);
      if (opener) opener.setAttribute('aria-expanded', 'false');
      /* Closing because the viewport grew leaves the phone-only opener
         hidden; focus lands on the desktop toggle instead. */
      var target = opener && opener.getClientRects().length ? opener : root.querySelector('[data-coll-filters-toggle]');
      opener = null;
      if (target) {
        try {
          target.focus({ preventScroll: true });
        } catch (error) {
          target.focus();
        }
      }
    }

    function closeDrawer() {
      if (dialog && dialog.open) {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      }
      afterClose();
    }

    if (dialog) {
      /* Esc: close through our path so the cleanup does not wait on the
         `close` event. */
      dialog.addEventListener(
        'cancel',
        function (event) {
          event.preventDefault();
          closeDrawer();
        },
        options
      );
      /* `close` is queued, not synchronous: a quick close + reopen can deliver
         the old event to a dialog that is open again, which must not strip
         the form out of it. */
      dialog.addEventListener(
        'close',
        function () {
          if (!dialog.open) afterClose();
        },
        options
      );
    }

    var desktop = window.matchMedia ? window.matchMedia(DESKTOP_QUERY) : null;
    function onDesktopChange() {
      if (desktop.matches && drawerOpen) closeDrawer();
    }
    if (desktop) {
      if (typeof desktop.addEventListener === 'function') desktop.addEventListener('change', onDesktopChange);
      else if (typeof desktop.addListener === 'function') desktop.addListener(onDesktopChange);
    }

    /* ---- fetch + render ---- */

    function render(nextRoot, scroll) {
      stripSectionId(nextRoot);
      /* Containers first: the clear links left afterwards are the ones
         outside them (the drawer footer). */
      syncAll(root, nextRoot, '[data-coll-results]');
      syncAll(root, nextRoot, '[data-coll-active]');
      syncAll(root, nextRoot, '[data-coll-count]');
      syncAll(root, nextRoot, '[data-coll-active-count]');
      syncAll(root, nextRoot, '[data-coll-show-results]');

      var clears = loneClears(root);
      var nextClears = loneClears(nextRoot);
      var fallback = nextRoot.querySelector('[data-coll-clear]');
      each(clears, function (link, index) {
        var source = nextClears[index] || fallback;
        if (!source) return;
        if (source.hasAttribute('href')) link.setAttribute('href', source.getAttribute('href'));
        link.hidden = source.hidden;
      });

      /* Groups are matched by param name, not order: a filter can appear or
         vanish between result sets. A price group holding focus keeps its
         body (the shopper may be typing); its count still updates. Any other
         focused group is swapped and focus goes back to the same input id,
         so a clicked checkbox's neighbours get fresh counts.
         `<details open>` lives on the group, which is never replaced. */
      var active = document.activeElement;
      var nextGroups = nextRoot.querySelectorAll('[data-coll-group]');
      each(root.querySelectorAll('[data-coll-group]'), function (group) {
        var key = group.getAttribute('data-coll-group');
        var nextGroup = null;
        for (var i = 0; i < nextGroups.length; i++) {
          if (nextGroups[i].getAttribute('data-coll-group') === key) {
            nextGroup = nextGroups[i];
            break;
          }
        }
        if (!nextGroup) return;
        var count = group.querySelector('[data-coll-group-count]');
        var nextCount = nextGroup.querySelector('[data-coll-group-count]');
        if (count && nextCount) syncElement(count, nextCount);
        var hasFocus = active && group.contains(active);
        if (hasFocus && active.closest('[data-coll-price]')) return;
        var body = group.querySelector('[data-coll-group-body]');
        var nextBody = nextGroup.querySelector('[data-coll-group-body]');
        if (body && nextBody) syncElement(body, nextBody);
        if (hasFocus && active.id && !group.contains(active)) {
          var again = document.getElementById(active.id);
          if (again) again.focus({ preventScroll: true });
        }
      });

      /* The select is outside the swapped parts; back/forward and links can
         change the sort, so it follows the fetched state. */
      var sort = root.querySelector('[data-coll-sort]');
      var nextSort = nextRoot.querySelector('[data-coll-sort]');
      if (sort && nextSort && sort.value !== nextSort.value) sort.value = nextSort.value;

      each(root.querySelectorAll('[data-coll-price]'), initPrice);

      if (scroll) {
        var toolbar = root.querySelector('.m-coll-toolbar');
        if (toolbar) {
          try {
            toolbar.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
          } catch (error) {
            toolbar.scrollIntoView(true);
          }
        }
        /* The pagination link that had focus was just replaced; keyboard
           users restart from the top of the new page. */
        var results = root.querySelector('[data-coll-results]');
        if (results && (!document.activeElement || document.activeElement === document.body)) {
          results.setAttribute('tabindex', '-1');
          results.focus({ preventScroll: true });
        }
      }
    }

    function finishLoading() {
      root.classList.remove('is-loading');
      var results = root.querySelector('[data-coll-results]');
      if (results) results.removeAttribute('aria-busy');
    }

    /* No-JS path: a full navigation. From `popstate` the address bar already
       shows the target, so it is replaced rather than pushed again. */
    function navigate(url, fromHistory) {
      if (fromHistory) location.replace(url);
      else location.assign(url);
    }

    function load(pathname, params, opts) {
      var query = params.toString();
      var url = pathname + (query ? '?' + query : '');
      if (url === currentUrl) return;
      currentUrl = url;

      if (typeof window.fetch !== 'function' || typeof DOMParser !== 'function') {
        navigate(url, opts.fromHistory);
        return;
      }

      if (request) request.abort();
      request = typeof AbortController === 'function' ? new AbortController() : null;
      var token = ++sequence;

      root.classList.add('is-loading');
      var results = root.querySelector('[data-coll-results]');
      if (results) results.setAttribute('aria-busy', 'true');

      var fetchUrl = pathname + '?section_id=' + encodeURIComponent(sectionId) + (query ? '&' + query : '');
      window
        .fetch(fetchUrl, request ? { signal: request.signal } : {})
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          if (token !== sequence) return;
          var nextRoot = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-coll]');
          /* A different template answering (a link to another collection
             using another section) has no matching root. */
          if (!nextRoot) throw new Error('No [data-coll] in response');
          render(nextRoot, opts.scroll);
          if (!opts.fromHistory) {
            var state = { collBento: true };
            if (location.pathname + location.search === url) history.replaceState(state, '', url);
            else history.pushState(state, '', url);
          }
          basePath = pathname;
          request = null;
          finishLoading();
          document.dispatchEvent(new CustomEvent('collection-bento:updated', { detail: { sectionId: sectionId } }));
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          if (token !== sequence) return;
          finishLoading();
          navigate(url, opts.fromHistory);
        });
    }

    /* The form is the source of truth (filters + `sort_by` through the
       select's `form` attribute); `page` is dropped so filtering starts
       again at page 1. */
    function loadFromForm() {
      clearTimeout(priceTimer);
      priceTimer = null;
      var form = getForm();
      var raw = new URLSearchParams();
      if (form) {
        new FormData(form).forEach(function (value, key) {
          if (typeof value === 'string') raw.append(key, value);
        });
      }
      /* Without a form (a collection with no filters to show) the select
         has nothing to join; it is read directly. */
      var sort = root.querySelector('[data-coll-sort]');
      if (sort && (!form || sort.form !== form) && sort.value) raw.set(sort.name || 'sort_by', sort.value);
      var params = cleanParams(raw);
      params.delete('page');
      load(location.pathname, params, {});
    }

    function schedulePrice() {
      clearTimeout(priceTimer);
      priceTimer = setTimeout(loadFromForm, PRICE_DELAY);
    }

    /* ---- load more ---- */

    function pageUrl(page) {
      var params = cleanParams(new URLSearchParams(location.search));
      if (page > 1) params.set('page', String(page));
      else params.delete('page');
      var query = params.toString();
      return location.pathname + (query ? '?' + query : '');
    }

    function fetchPage(page) {
      var params = cleanParams(new URLSearchParams(location.search));
      params.set('page', String(page));
      return window
        .fetch(location.pathname + '?section_id=' + encodeURIComponent(sectionId) + '&' + params.toString())
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var nextRoot = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-coll]');
          if (!nextRoot || !nextRoot.querySelector('[data-coll-grid]')) throw new Error('No grid in response');
          stripSectionId(nextRoot);
          return nextRoot;
        });
    }

    /* The toolbar's "Showing 1–48 of 120": the same translated string with
       the placeholders filled, so it reads right for any stretch of pages. */
    function paintRange(from, to) {
      var count = root.querySelector('[data-coll-count]');
      var template = count && count.getAttribute('data-range');
      if (!template) return;
      if (from !== null) count.setAttribute('data-from', from);
      if (to !== null) count.setAttribute('data-to', to);
      count.textContent = template.replace('[from]', count.getAttribute('data-from')).replace('[to]', count.getAttribute('data-to'));
    }

    function swapSlot(which, nextRoot) {
      var slot = root.querySelector('[data-coll-more-slot="' + which + '"]');
      var nextSlot = nextRoot.querySelector('[data-coll-more-slot="' + which + '"]');
      if (slot && nextSlot) slot.replaceWith(nextSlot);
      else if (slot) slot.remove();
    }

    /* Earlier pages above the grid; the view stays on the card that was at
       its top, so nothing jumps. `roots` are in page order. */
    function prependPages(roots) {
      var grid = root.querySelector('[data-coll-grid]');
      var anchor = grid.firstElementChild;
      var before = anchor ? anchor.getBoundingClientRect().top : 0;
      var slot = root.querySelector('[data-coll-more-slot="previous"]');
      var focusInSlot = !!(slot && slot.contains(document.activeElement));
      var fragment = document.createDocumentFragment();
      var added = [];
      roots.forEach(function (nextRoot) {
        each(Array.prototype.slice.call(nextRoot.querySelector('[data-coll-grid]').children), function (item) {
          added.push(item);
          fragment.appendChild(item);
        });
      });
      grid.insertBefore(fragment, anchor);
      var first = roots[0];
      grid.setAttribute('data-first-page', first.querySelector('[data-coll-grid]').getAttribute('data-first-page'));
      /* Every change above the anchor first (the slot shrinks away on page
         1), then one correction, so nothing on screen moves. */
      swapSlot('previous', first);
      var count = first.querySelector('[data-coll-count]');
      paintRange(count ? count.getAttribute('data-from') : null, null);
      if (anchor) window.scrollBy(0, anchor.getBoundingClientRect().top - before);
      if (focusInSlot) {
        var nextSlotLink = root.querySelector('[data-coll-more="previous"]');
        var firstLink = added[0] && added[0].querySelector('a[href]:not([tabindex="-1"])');
        var target = nextSlotLink || firstLink;
        if (target) target.focus({ preventScroll: true });
      }
    }

    function loadMore(link, which) {
      if (moreBusy || typeof window.fetch !== 'function' || typeof DOMParser !== 'function') return false;
      var grid = root.querySelector('[data-coll-grid]');
      if (!grid) return false;
      var page = Number(grid.getAttribute(which === 'next' ? 'data-last-page' : 'data-first-page')) + (which === 'next' ? 1 : -1);
      if (!(page >= 1)) return false;
      moreBusy = true;
      var token = sequence;
      link.classList.add('is-loading');
      link.setAttribute('aria-disabled', 'true');
      grid.setAttribute('aria-busy', 'true');
      fetchPage(page)
        .then(function (nextRoot) {
          if (token !== sequence) return;
          if (which === 'previous') {
            prependPages([nextRoot]);
          } else {
            var items = Array.prototype.slice.call(nextRoot.querySelector('[data-coll-grid]').children);
            each(items, function (item) {
              grid.appendChild(item);
            });
            grid.setAttribute('data-last-page', String(page));
            swapSlot('next', nextRoot);
            var count = nextRoot.querySelector('[data-coll-count]');
            paintRange(null, count ? count.getAttribute('data-to') : null);
            currentUrl = pageUrl(page);
            history.replaceState(history.state || { collBento: true }, '', currentUrl);
            /* Keyboard users go on from the first new card. */
            var focusable = items[0] && items[0].querySelector('a[href]:not([tabindex="-1"])');
            if (focusable) focusable.focus({ preventScroll: true });
          }
        })
        .catch(function () {
          if (token === sequence) location.assign(link.href);
        })
        .then(function () {
          moreBusy = false;
          link.classList.remove('is-loading');
          link.removeAttribute('aria-disabled');
          grid.removeAttribute('aria-busy');
        });
      return true;
    }

    function readMore() {
      try {
        return JSON.parse(window.sessionStorage.getItem(MORE_KEY) || 'null');
      } catch (error) {
        return null;
      }
    }

    /* Kept only while earlier pages are stacked above the one in the
       address: that is the case a fresh load of the address cannot show. */
    function saveMore() {
      var grid = root.querySelector('[data-coll-grid]');
      try {
        if (grid && Number(grid.getAttribute('data-first-page')) < Number(grid.getAttribute('data-last-page'))) {
          window.sessionStorage.setItem(
            MORE_KEY,
            JSON.stringify({
              url: location.pathname + location.search,
              first: Number(grid.getAttribute('data-first-page')),
              scroll: Math.round(window.scrollY)
            })
          );
        } else {
          window.sessionStorage.removeItem(MORE_KEY);
        }
      } catch (error) {
        /* Not remembered: coming back shows the page in the address. */
      }
    }

    function restoreMore() {
      var saved = readMore();
      var grid = root.querySelector('[data-coll-grid]');
      if (!saved || !grid || saved.url !== location.pathname + location.search) return;
      try {
        window.sessionStorage.removeItem(MORE_KEY);
      } catch (error) {
        /* Nothing to clean. */
      }
      var shown = Number(grid.getAttribute('data-first-page'));
      if (!(saved.first >= 1 && saved.first < shown) || typeof window.fetch !== 'function') return;
      /* The browser would restore the scroll before the earlier pages are
         back; this entry is put back by hand instead. */
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      moreBusy = true;
      var token = sequence;
      var pages = [];
      for (var page = saved.first; page < shown; page++) pages.push(fetchPage(page));
      Promise.all(pages)
        .then(function (roots) {
          if (token !== sequence) return;
          prependPages(roots);
          window.scrollTo(0, saved.scroll);
        })
        .catch(function () {
          /* The page in the address stays, with its "previous" button. */
        })
        .then(function () {
          moreBusy = false;
          if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
        });
    }

    /* ---- events ---- */

    root.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        if (!target || !target.closest) return;

        var more = target.closest('a[data-coll-more]');
        if (more && root.contains(more)) {
          if (event.defaultPrevented || event.button !== 0) return;
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          if (more.getAttribute('aria-disabled') === 'true') {
            event.preventDefault();
            return;
          }
          if (loadMore(more, more.getAttribute('data-coll-more'))) event.preventDefault();
          return;
        }

        var link = target.closest('a[data-coll-link]');
        if (link && root.contains(link)) {
          if (event.defaultPrevented || event.button !== 0) return;
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          if ((link.target && link.target !== '_self') || link.origin !== location.origin) return;
          event.preventDefault();
          var params = cleanParams(new URLSearchParams(link.search));
          var isPage = !!link.closest('[data-coll-results]') && params.has('page');
          load(link.pathname, params, { scroll: isPage });
          return;
        }

        if (target.closest('[data-coll-filters-toggle]')) {
          toggleFilters();
          return;
        }

        var viewButton = target.closest('[data-coll-view]');
        if (viewButton && root.contains(viewButton)) {
          chooseView(viewButton);
          return;
        }

        var colsButton = target.closest('[data-coll-cols]');
        if (colsButton && root.contains(colsButton)) {
          chooseCols(colsButton);
          return;
        }

        var openButton = target.closest('[data-coll-drawer-open]');
        if (openButton) {
          openDrawer(openButton);
          return;
        }

        /* A click on the dialog box itself is a click on its backdrop: the
           drawer's content covers the rest of the box. */
        if (target.closest('[data-coll-drawer-close]') || (dialog && target === dialog)) {
          closeDrawer();
        }
      },
      options
    );

    root.addEventListener(
      'change',
      function (event) {
        var target = event.target;
        if (!target || !target.matches) return;
        if (target.matches('[data-coll-sort]')) {
          loadFromForm();
        } else if (target.type === 'checkbox' && target.closest('[data-coll-form]')) {
          loadFromForm();
        } else if (target.matches('[data-coll-price-input]')) {
          normalisePrice(target.closest('[data-coll-price]'));
          schedulePrice();
        }
      },
      options
    );

    root.addEventListener(
      'input',
      function (event) {
        var target = event.target;
        if (!target || !target.matches) return;
        if (target.matches('[data-coll-price-range]')) {
          onRangeInput(target);
          schedulePrice();
        } else if (target.matches('[data-coll-price-input]')) {
          rangesFromInputs(priceParts(target.closest('[data-coll-price]')));
          schedulePrice();
        }
      },
      options
    );

    /* Enter in a price field submits the GET form; keep it in place. */
    root.addEventListener(
      'submit',
      function (event) {
        if (!event.target.matches || !event.target.matches('[data-coll-form]')) return;
        event.preventDefault();
        each(root.querySelectorAll('[data-coll-price]'), normalisePrice);
        loadFromForm();
      },
      options
    );

    /* Covers the entry before the first pushState too (its state is null).
       Another script's history entries (a quick view pushing a product URL)
       are left alone by the pathname check. */
    function onPopState() {
      if (!document.documentElement.contains(root)) return;
      if (location.pathname !== basePath) return;
      load(location.pathname, cleanParams(new URLSearchParams(location.search)), { fromHistory: true });
    }
    window.addEventListener('popstate', onPopState);
    window.addEventListener('pagehide', saveMore);

    /* ---- boot ---- */

    applyHidden(readHidden(root.hasAttribute('data-filters-default-hidden')));
    each(root.querySelectorAll('[data-coll-price]'), initPrice);
    restoreMore();
    if (root.querySelector('[data-coll-cols]')) paintCols();
    if (root.querySelector('[data-coll-view]')) paintView();

    return {
      teardown: function () {
        if (controller) controller.abort();
        if (request) request.abort();
        clearTimeout(priceTimer);
        window.removeEventListener('popstate', onPopState);
        window.removeEventListener('pagehide', saveMore);
        if (colsObserver) colsObserver.disconnect();
        if (colsFrame) window.clearTimeout(colsFrame);
        colsQueries.forEach(function (list) {
          if (!list) return;
          if (typeof list.removeEventListener === 'function') list.removeEventListener('change', scheduleCols);
          else if (typeof list.removeListener === 'function') list.removeListener(scheduleCols);
        });
        if (desktop) {
          if (typeof desktop.removeEventListener === 'function') desktop.removeEventListener('change', onDesktopChange);
          else if (typeof desktop.removeListener === 'function') desktop.removeListener(onDesktopChange);
        }
        if (drawerOpen) {
          drawerOpen = false;
          document.documentElement.classList.remove(LOCK_CLASS);
        }
      }
    };
  }

  function boot() {
    each(document.querySelectorAll('[data-coll]'), function (root) {
      if (root.dataset.collInit === 'true') return;
      root.dataset.collInit = 'true';
      var id = root.getAttribute('data-section-id') || '';
      if (instances[id]) instances[id].teardown();
      instances[id] = init(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', function (event) {
    var id = event.detail && event.detail.sectionId;
    if (id && instances[id]) {
      instances[id].teardown();
      delete instances[id];
    }
  });
})();
